import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import ContextualBalloon from '@ckeditor/ckeditor5-ui/src/panel/balloon/contextualballoon';
import clickOutsideHandler from '@ckeditor/ckeditor5-ui/src/bindings/clickoutsidehandler';
import ContentStylesFormView from './content-styles-form';
import ClickObserver from '@ckeditor/ckeditor5-engine/src/view/observer/clickobserver';
import ContentStylesCommand from './content-styles-command';
import ContentStylesIcon from '../theme/icons/content-styles.svg';

import '../theme/content-styles.css';

const CONTENTSTYLES = 'contentStyles';

export default class ContentStylesUI extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [ ContextualBalloon ];
	}

	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'ContentStylesUI';
	}

	init() {
		const editor = this.editor;
		const config = editor.config.get( CONTENTSTYLES );
		const styles = config && config.styles;

		editor.conversion.for( 'downcast' ).add( dispatcher => {
			dispatcher.on(
				'attribute:classes',
				( evt, data, conversionApi ) => {
					const viewWriter = conversionApi.writer;
					const element = conversionApi.mapper.toViewElement(
						data.item
					);
					const classes = element.getClassNames();

					if ( classes ) {
						const classesArray = Array.from( classes );

						for ( const c of classesArray ) {
							viewWriter.removeClass( c, element );
						}
					}

					if ( data.attributeNewValue ) {
						const newClasses = data.attributeNewValue.split( ' ' );

						for ( const c of newClasses ) {
							viewWriter.addClass( c, element );
						}
					}
				},
				{ priority: 'low' }
			);
		} );

		if ( !styles || !Array.isArray( styles ) || !styles.length ) {
			throw new Error(
				'There are no styles defined in the contentStyles configuration'
			);
		}
		// Save a reference to the content styles collection.
		this.styles = styles;

		editor.editing.view.addObserver( ClickObserver );

		editor.commands.add(
			CONTENTSTYLES,
			new ContentStylesCommand( editor, styles )
		);

		// The form view displayed inside the balloon.
		this.formView = this._createFormView();

		this._balloon = editor.plugins.get( ContextualBalloon );

		// Create toolbar buttons.
		this._createToolbarButton();

		// Attach lifecycle actions to the the balloon.
		this._enableUserBalloonInteractions();
	}

	/**
	 * @inheritDoc
	 */
	destroy() {
		super.destroy();

		// Destroy created UI components as they are not automatically destroyed (see ckeditor5#1341).
		this.formView.destroy();
	}

	/**
	 * Creates the {@link module:link/ui/linkformview~LinkFormView} instance.
	 *
	 * @private
	 * @returns {module:link/ui/linkformview~LinkFormView} The link form view instance.
	 */
	_createFormView() {
		const editor = this.editor;
		const contentStylesCommand = editor.commands.get( CONTENTSTYLES );

		const formView = new ContentStylesFormView(
			editor.locale,
			contentStylesCommand
		);
		// Form elements should be read-only when corresponding commands are disabled.
		formView.bind( 'isEnabled' ).to( contentStylesCommand );
		formView.resetButtonView.bind( 'isEnabled' ).to( contentStylesCommand );
		formView.cancelButtonView.bind( 'isEnabled' ).to( contentStylesCommand );

		// Execute command when submitting the form.
		this.listenTo( formView, 'submit', () => {
			contentStylesCommand.execute();
			this._closeFormView();
		} );

		// Hide the panel after clicking the "Reset" button.
		this.listenTo( formView, 'reset', () => {
			// Remove the element class.
			contentStylesCommand.value = '';
			contentStylesCommand.execute();
			this._closeFormView();
		} );

		// Hide the panel after clicking the "Cancel" button.
		this.listenTo( formView, 'cancel', () => {
			this._closeFormView();
		} );

		// Close the panel on esc key press when the **form has focus**.
		formView.keystrokes.set( 'Esc', ( data, cancel ) => {
			this._closeFormView();
			cancel();
		} );

		return formView;
	}

	/**
	 * Add the `contentStyles` button to feature components.
	 */
	_createToolbarButton() {
		const editor = this.editor;
		const t = this.editor.t;
		editor.ui.componentFactory.add( CONTENTSTYLES, locale => {
			const button = new ButtonView( locale );

			button.set( {
				label: t( 'Styles' ),
				tooltip: true,
				isEnabled: true,
				icon: ContentStylesIcon,
			} );

			const command = editor.commands.get( CONTENTSTYLES );
			button.bind( 'isEnabled' ).to( command, 'isEnabled' );
			button.bind( 'isOn' ).to( command, 'value', value => !!value );

			// Show the panel on button click.
			this.listenTo( button, 'execute', () => this._showUI( true ) );

			return button;
		} );
	}

	/**
	 * Attaches actions that control whether the balloon panel containing the
	 * {@link #formView} is visible or not.
	 *
	 * @private
	 */
	_enableUserBalloonInteractions() {
		// Close the panel on the Esc key press when the editable has focus and the balloon is visible.
		this.editor.keystrokes.set( 'Esc', ( data, cancel ) => {
			if ( this._isUIVisible ) {
				this._hideUI();
				cancel();
			}
		} );

		// Close on click outside of balloon panel element.
		clickOutsideHandler( {
			emitter: this.formView,
			activator: () => this._isFormInPanel,
			contextElements: [ this._balloon.view.element ],
			callback: () => this._hideUI()
		} );
	}

	/**
	 * Adds the {@link #formView} to the {@link #_balloon}.
	 *
	 * @protected
	 */
	_addFormView() {
		if ( this._isFormInPanel ) {
			return;
		}

		this.formView.disableCssTransitions();

		this._balloon.add( {
			view: this.formView,
			position: this._getBalloonPositionData()
		} );

		this.formView.enableCssTransitions();
	}

	getSelectionParent( viewDocument ) {
		return viewDocument.selection.focus
			.getAncestors()
			.reverse()
			.find( node => node.is( 'element' ) );
	}

	/**
	 * Closes the form view. Decides whether the balloon should be hidden completely or if the action view should be shown. This is
	 * decided upon the link command value (which has a value if the document selection is in the link).
	 *
	 * Additionally, if any {@link module:link/link~LinkConfig#decorators} are defined in the editor configuration, the state of
	 * switch buttons responsible for manual decorator handling is restored.
	 *
	 * @private
	 */
	_closeFormView() {
		const contentStylesCommand = this.editor.commands.get( CONTENTSTYLES );

		// Restore state to represent the current model state. This case is important to reset the selected radio button
		// when the user cancels the editing form.
		// contentStylesCommand.restoreState();

		if ( contentStylesCommand.value !== undefined ) {
			this._removeFormView();
		} else {
			this._hideUI();
		}
	}

	/**
	 * Removes the {@link #formView} from the {@link #_balloon}.
	 *
	 * @protected
	 */
	_removeFormView() {
		if ( this._isFormInPanel ) {
			// Blur the input element before removing it from DOM to prevent issues in some browsers.
			// See https://github.com/ckeditor/ckeditor5/issues/1501.
			this.formView.saveButtonView.focus();

			this._balloon.remove( this.formView );

			// Because the form has an input which has focus, the focus must be brought back
			// to the editor. Otherwise, it would be lost.
			this.editor.editing.view.focus();
		}
	}

	/**
	 * Shows the UI.
	 *
	 * @param {Boolean} forceVisible
	 * @private
	 */
	_showUI( forceVisible = false ) {
		this._addFormView();

		// Be sure panel with link is visible.
		if ( forceVisible ) {
			this._balloon.showStack( 'main' );
		}

		// Begin responding to ui#update once the UI is added.
		this._startUpdatingUI();
	}

	/**
	 * Removes the {@link #formView} from the {@link #_balloon}.
	 *
	 * See {@link #_addFormView}, {@link #_addActionsView}.
	 *
	 * @protected
	 */
	_hideUI() {
		if ( !this._isFormInPanel ) {
			return;
		}

		const editor = this.editor;

		this.stopListening( editor.ui, 'update' );
		this.stopListening( this._balloon, 'change:visibleView' );

		// Make sure the focus always gets back to the editable _before_ removing the focused form view.
		// Doing otherwise causes issues in some browsers. See https://github.com/ckeditor/ckeditor5-link/issues/193.
		editor.editing.view.focus();

		// Remove the form view.
		this._balloon.remove( this.formView );

		// Focus in the editing view again.
		editor.editing.view.focus();
	}

	/**
	 * Makes the UI react to the {@link module:core/editor/editorui~EditorUI#event:update} event to
	 * reposition itself when the editor UI should be refreshed.
	 *
	 * See: {@link #_hideUI} to learn when the UI stops reacting to the `update` event.
	 *
	 * @protected
	 */
	_startUpdatingUI() {
		const editor = this.editor;

		const update = () => {
			// Get the current element.
			const selectedElement = this._getContentStyleElement();
			if ( selectedElement && this._isUIVisible ) {
				// Update the balloon position.
				this._balloon.updatePosition( this._getBalloonPositionData() );
			}
		};

		this.listenTo( editor.ui, 'update', update );
		this.listenTo( this._balloon, 'change:visibleView', update );
	}

	/**
	 * Returns `true` when {@link #formView} is in the {@link #_balloon}.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _isFormInPanel() {
		return this._balloon.hasView( this.formView );
	}

	/**
	 * Returns `true` when {@link #formView} is in the {@link #_balloon} and it is
	 * currently visible.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _isUIVisible() {
		const visibleView = this._balloon.visibleView;
		return visibleView == this.formView;
	}

	/**
	 * Returns positioning options for the {@link #_balloon}. They control the way the balloon is attached
	 * to the target element or selection.
	 *
	 * @private
	 * @returns {module:utils/dom/position~Options}
	 */
	_getBalloonPositionData() {
		const view = this.editor.editing.view;
		const viewDocument = view.document;
		let target = null;

		// Make sure the target is calculated on demand at the last moment because a cached DOM range
		// (which is very fragile) can desynchronize with the state of the editing view if there was
		// any rendering done in the meantime. This can happen, for instance, when an inline widget
		// gets unlinked.
		target = () => {
			const targetElement = this._getContentStyleElement();

			return targetElement ? // When selection is inside link element, then attach panel to this element.
				view.domConverter.mapViewToDom( targetElement ) : // Otherwise attach panel to the selection.
				view.domConverter.viewRangeToDom(
					viewDocument.selection.getFirstRange()
				  );
		};

		return { target };
	}

	/**
	 * Based on the current selection, find a node that matches a content style rule.
	 *
	 * @returns {Element}
	 */
	_getContentStyleElement() {
		const view = this.editor.editing.view;
		const selection = view.document.selection;
		const parents = selection.getFirstPosition().getAncestors();
		for ( const style of this.styles ) {
			const element = parents.find( p => p.name === style.tag );
			if ( !element ) {
				continue;
			}
			const parent = element.parent;
			if ( parent && parent.hasClass( style.zone ) ) {
				return element;
			}
		}
		return null;
	}
}
